import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import {useContext, useEffect, useState} from "react";
import * as Yup from 'yup';
import { useClusterStoresRepository } from "../../../api/repositories/ClusterStoresRepository";
import { useSetRecoilState } from "recoil";
import { STATE_STORES_TABLE_UPDATING } from '../constants/ClustersRecoil'
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import FormAsyncAutocomplete from "../../../experiments/forms/FormAsyncAutocomplete";
import { MobilityStore } from "../../mobilitystore/constants/MobilityStoreModels";
import { GlobalContext } from '../../../../../util/state/GlobalContextProvider';

interface StoreCreateModalProps {
    open: boolean,
    onClose: Function,
    clusterId: number,
    setOpenErrorDialog: Function,
    setMessage: Function,
    legalEntity: string,
    dealer: string
}

interface MsInput {
    stores: string[]
}

const StoreCreateModal = (props: StoreCreateModalProps) => {
    const { t } = useTranslation('finance', { keyPrefix: 'clusters' });
    const { createStore } = useClusterStoresRepository();
    const setUpdating = useSetRecoilState(STATE_STORES_TABLE_UPDATING);
    const {  globalData: { user } } = useContext(GlobalContext);

    const [stores, setStores] = useState<MobilityStore[]>([]);

    const schema = Yup.object().shape({
        stores: Yup.array().required()
    });


    const defaultValues = {

        stores: [] as Array<any>,
    }

    useEffect(() => {

        const mobilityStores: MobilityStore[] = (user.mobilityStores || [])
            .filter((dms: any)=>    dms.representativeInfo &&
                dms.financialInfo?.feesDued &&
                dms.tenant == props.legalEntity
            )
            .map((dms: any) => {
                return {
                    id: dms.id,
                    tenant: dms.tenant,
                    name: dms.name,
                    code: dms.identificationCode,
                    type: dms.type,
                    sapId: dms.sapId,
                    msVendorId: dms.representativeInfo.idSapRepresentative,
                    msVendorName: dms.representativeInfo.companyName || dms.representativeInfo.nameAndSurnameOfTheContactPerson,
                    msVendorEmail: dms.representativeInfo.emailOfRepresentative
                }
            })

        sortStores(mobilityStores);
        setStores(mobilityStores);
    }, [user, props.legalEntity])

    const { handleSubmit, control, watch, reset, clearErrors } = useForm<MsInput>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });

    const watcher = watch();

    const onSubmit: SubmitHandler<MsInput> = data => {
        let ms: any[] = data.stores.length === 0 ? stores : data.stores.map((it: any) => stores.find(store => store.sapId === it.value.sapId));

        props.onClose();
        cleanForm();
        createStore(props.clusterId, ms, props.legalEntity, props.dealer).then((res) => {
            if (res.result.storesInCurrent.length === 0 && res.result.storesInOthers.length === 0) {
                setUpdating(true);
            } else {
                props.setOpenErrorDialog(true);
                let message = '';
                if (res.result.storesInCurrent.length > 0) {
                    message += t(`error-dialog.errors.cluster-association.already-here`, {
                        msList: res.result.storesInCurrent.slice(0, 5)
                            .reduce((prev: string, curr: string, i: number) => {
                                if (i === 0)
                                    return prev.concat(`'${curr}'`)
                                else
                                    return prev.concat(`, '${curr}'`)
                            }, ''),
                        count: res.result.storesInCurrent.length
                    }) + '\n'
                }
                if (res.result.storesInOthers.length > 0) {
                    if (res.result.storesInOthers.every((curr: any) => curr.key === res.result.storesInOthers[0].key)) {
                        message += t(`error-dialog.errors.cluster-association.already-there`, {
                            msList: res.result.storesInOthers.slice(0, 5)
                                .reduce((prev: any, curr: any, i: number) => {
                                    if (i === 0)
                                        return prev.concat(`'${curr.value}'`)
                                    else
                                        return prev.concat(`, '${curr.value}'`)
                                }, ''),
                            key: res.result.storesInOthers[0].key,
                            count: res.result.storesInOthers.length
                        }) + '\n';
                    } else {
                        res.result.storesInOthers.slice(0, 5).forEach((ms: any) => {
                            console.log(ms)
                            message += t(`error-dialog.errors.cluster-association.already-there`, {
                                msList: ms.value,
                                key: ms.key,
                                count: 1
                            }) + '\n';
                        })
                    }
                    message += t(`error-dialog.errors.cluster-association.already-there-footer`);
                }
                props.setMessage(message)
            }
        })
    }

    const cleanForm = () => {
        reset(defaultValues);
        clearErrors();
    }

    function sortStores(stores: any[]) {
        if (!Array.isArray(stores) || !stores.every(store => store.hasOwnProperty('name'))) {
            return;
        }
        stores.sort((a: any, b: any) => a.name.localeCompare(b.name));
    }


    return (
        <Dialog maxWidth={"lg"} disableEscapeKeyDown fullWidth={true} open={props.open} onClose={() => {
            return;
        }}>
            <DialogTitle>
                <Typography fontWeight={"bold"} color={"primary"}>
                    {t('mobility-store-modal.title.add')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        cleanForm();
                        props.onClose();
                    }}
                    color={"primary"}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <HighlightOffRoundedIcon />
                </IconButton>
            </DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    <Grid container spacing={2} sx={{ padding: "15px" }} alignItems={"center"}>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormAsyncAutocomplete
                                name={'stores'}
                                control={control}
                                props={{
                                    getOptionLabel: (it) => `${it.value.name} | (${it.value.sapId})`,
                                    callback: () => Promise.resolve(() => {
                                        return stores.map((it) => {
                                            return { key: it.sapId, value: it };
                                        });
                                    }),
                                    initialValues: true,
                                    multiselect: true,
                                    renderInput: (params) =>
                                        <TextField
                                            {...params}
                                            label={t('mobility-store-modal.mobility-store')}
                                        />
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ padding: "10px" }}>
                    <Button variant={"outlined"} color={"accent"}
                        onClick={() => {
                            cleanForm();
                            props.onClose()
                        }}>{t('mobility-store-modal.reset')}</Button>
                    <Button color={"accent"} disabled={watcher.stores.length === 0} type="submit">{t('mobility-store-modal.apply')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
export default StoreCreateModal;